"use client"

import { useState } from "react"
import { Outlet, useNavigate } from "@tanstack/react-router"
import { Sidebar } from "./sidebar"
import { ThemeProvider } from "@/shared/components/theme-provider"
import { Toaster } from "@/shared/components/ui/sonner"
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar"
import { ChevronDown, Fingerprint, KeyRound, LogOut } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu"
import { useAdminAuthStore } from "@/domains/admin/stores"
import { usePermissions } from "@/shared/hooks/use-permissions"
import { USER_ROLES } from "@shared/constants/user-constants"
import { cn } from "@shared/lib/utils"
import { ChangePasswordDialog } from "@/shared/components/change-password-dialog"

export function MainLayout() {
  const { user, logoutAndClearCache } = useAdminAuthStore()
  const navigate = useNavigate()
  const { userRole, canViewTransactions, canViewReports } = usePermissions()
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false)
  
  const handleLogout = async () => {
    await logoutAndClearCache()
    navigate({ to: '/auth/login' })
  }

  return (
    <ThemeProvider defaultTheme="light" storageKey="admin-portal-theme">
      <div className="flex min-h-screen flex-col">
        {/* Header - sticky when scroll */}
        <header
          className="sticky top-0 z-30 flex-shrink-0 h-14 bg-primary text-white px-4 py-2 flex justify-end lg:justify-between items-center"
          style={{ ['--header-height' as string]: '3.5rem' }}
        >
          <div className="lg:flex items-center space-x-2 hidden">
            <Fingerprint className="h-6 w-6" />
            <span className="font-bold text-lg">ADMIN PORTAL</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center space-x-2 focus:outline-none">
              <Avatar>
                {user?.avatar ? (
                  <AvatarImage src={user.avatar || "/placeholder.svg"} alt={user.userName} />
                ) : (
                  <AvatarFallback>{user?.userName.charAt(0) || "U"}</AvatarFallback>
                )}
              </Avatar>
              <div className="flex items-center">
                <span className="font-medium">{user?.userName || "User"}</span>
                <ChevronDown className="h-4 w-4 ml-1" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setIsChangePasswordOpen(true)}>
                <KeyRound className="h-4 w-4 mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <div className="flex flex-1">
          {(() => {
            const isUser = userRole === USER_ROLES.USER
            const isViewer = userRole === USER_ROLES.VIEWER
            const shouldShowSidebar = isUser ? false : (isViewer ? (canViewTransactions && canViewReports) : true)
            
            return shouldShowSidebar ? <Sidebar /> : null
          })()}
          <div className={cn(
            "flex-1 min-w-0 bg-gray-50 transition-all duration-300 ease-in-out",
              userRole === USER_ROLES.VIEWER && !canViewTransactions && !canViewReports && "bg-yellow-50"
          )}>
            {userRole === USER_ROLES.VIEWER && !canViewTransactions && !canViewReports && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Limited Access:</strong> You don&apos;t have permission to view transactions or reports. 
                      Please contact your administrator to request additional permissions.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <main className="container mx-auto p-4 md:p-6">
              <Outlet />
            </main>
          </div>
        </div>
      </div>
      <Toaster />
      <ChangePasswordDialog 
        open={isChangePasswordOpen} 
        onOpenChange={setIsChangePasswordOpen} 
      />
    </ThemeProvider>
  )
}
